/**
 * Any-workflow DoD predicates on `/database/views` + `/database/objects`
 * (#46035 / Phase 3.E).
 *
 * A derived criterion is a saved_view (field-present, DEPENDENT) or an
 * `oracle:` ref (INDEPENDENT). proposal = source derived/learned and unbound
 * — it does NOT gate `done`. confirmed gates `done`. Conflating them is the
 * reject. This contract reads that distinction; confirm is an explicit POST
 * and refuses a live task unless `acknowledgeForeignLive` is sent.
 */

import { z } from "zod";
import type {
  DodPredicateConfirmView,
  DodPredicateListView,
  DodPredicateView,
} from "@excellent/core/dod";
import type { Operation } from "../../types";

export const predicateStanceSchema = z.enum(["proposal", "confirmed"]);
export const predicateIndependenceSchema = z.enum([
  "independent",
  "dependent",
  "unknown",
]);

export const dodPredicateRowSchema = z.object({
  uid: z.string(),
  id: z.number(),
  kind: z.string(),
  ref: z.string().nullable(),
  source: z.string(),
  why: z.string().nullable(),
  stance: predicateStanceSchema,
  binding: z.boolean(),
  bound: z.boolean(),
  boundAt: z.string().nullable(),
  independence: predicateIndependenceSchema,
  viewUid: z.string().nullable(),
  viewName: z.string().nullable(),
  entityKind: z.string().nullable(),
  taskId: z.number(),
  taskUid: z.string(),
  taskTitle: z.string(),
  taskStatus: z.string(),
  taskAssignee: z.string().nullable(),
  lastInProgressAssignee: z.string().nullable(),
  liveTask: z.boolean(),
  createdAt: z.string(),
});
export type DodPredicateRowDto = z.infer<typeof dodPredicateRowSchema>;

export const dodPredicateListSchema = z.object({
  readable: z.boolean(),
  unreadableReason: z.string().nullable(),
  predicates: z.array(dodPredicateRowSchema),
  proposalCount: z.number(),
  confirmedCount: z.number(),
  dependentCount: z.number(),
  independentCount: z.number(),
});
export type DodPredicateListDto = z.infer<typeof dodPredicateListSchema>;

export const dodPredicateListQuery = z.object({
  entity_kind: z.string().optional(),
  view_uid: z.string().optional(),
});
export type DodPredicateListQuery = z.infer<typeof dodPredicateListQuery>;

const dodPredicateListOp: Operation<
  void,
  DodPredicateListQuery,
  void,
  DodPredicateListDto
> = {
  method: "GET",
  path: () => "/api/database/dod-predicates",
  query: dodPredicateListQuery,
  response: dodPredicateListSchema,
};

export const dodPredicateConfirmBody = z.object({
  criterion: z.string().min(1),
  /** Required when the criterion sits on an in_progress/review task. */
  acknowledgeForeignLive: z.boolean().optional(),
});
export type DodPredicateConfirmBody = z.infer<typeof dodPredicateConfirmBody>;

export const dodPredicateConfirmSchema = z.object({
  ok: z.boolean(),
  reason: z.string().nullable(),
  alreadyBound: z.boolean(),
  list: dodPredicateListSchema,
});
export type DodPredicateConfirmDto = z.infer<typeof dodPredicateConfirmSchema>;

const dodPredicateConfirmOp: Operation<
  void,
  void,
  DodPredicateConfirmBody,
  DodPredicateConfirmDto
> = {
  method: "POST",
  path: () => "/api/database/dod-predicates/confirm",
  body: dodPredicateConfirmBody,
  response: dodPredicateConfirmSchema,
  invalidates: [dodPredicateListOp],
};

export const dodPredicates = {
  list: dodPredicateListOp,
  confirm: dodPredicateConfirmOp,
};

type _RowWire = DodPredicateRowDto extends DodPredicateView ? true : never;
type _RowReader = DodPredicateView extends DodPredicateRowDto ? true : never;
type _ListWire = DodPredicateListDto extends DodPredicateListView ? true : never;
type _ListReader = DodPredicateListView extends DodPredicateListDto ? true : never;
type _ConfirmWire = DodPredicateConfirmDto extends DodPredicateConfirmView ? true : never;
type _ConfirmReader = DodPredicateConfirmView extends DodPredicateConfirmDto ? true : never;
const _parity: [
  _RowWire,
  _RowReader,
  _ListWire,
  _ListReader,
  _ConfirmWire,
  _ConfirmReader,
] = [true, true, true, true, true, true];
void _parity;
